import LoggedUserIndicator from "../../02-molecules/logged-user-indicator/logged-user-indicator";
import { isVisible } from "@utils/isVisible";
import { handleCustomUDL } from "@utils/UDLUtils";

// ─── Constants ────────────────────────────────────────────────────────────────

const URL_PARAM_SEARCH = "search";

const CLASSES = {
  closed: "closed",
  active: "active",
};

const SELECTORS = {
  closeButton: ".js-close-button",
  burgerFlyout: ".js-navigation-flyout",
  siteSearchFlyout: ".js-site-search-flyout",
  siteSearchInput: ".js-v2-sitesearchfield-input",
  // document-scoped (live outside the component root)
  burgerButton: ".js-burger",
  siteSearchButton: ".js-site-search",
  singleLevelNav: ".single-level-navigation",
};

// ─── Class ────────────────────────────────────────────────────────────────────

export class Flyout {
  private el: HTMLElement;
  private abortController: AbortController;
  private hasSingleLevelNav: boolean;

  // Cached elements
  private closeButtons: NodeListOf<HTMLElement>;
  private burgerFlyout: HTMLElement | null;
  private siteSearchFlyout: HTMLElement | null;
  private siteSearchInput: HTMLInputElement | null;
  private burgerButtons: NodeListOf<HTMLElement>;
  private siteSearchButtons: NodeListOf<HTMLElement>;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
    this.hasSingleLevelNav = !!document.querySelector(SELECTORS.singleLevelNav);

    this.closeButtons =
      document.querySelectorAll<HTMLElement>(SELECTORS.closeButton);
    this.burgerFlyout = el.querySelector<HTMLElement>(SELECTORS.burgerFlyout);
    this.siteSearchFlyout = el.querySelector<HTMLElement>(
      SELECTORS.siteSearchFlyout,
    );
    this.siteSearchInput = el.querySelector<HTMLInputElement>(
      SELECTORS.siteSearchInput,
    );
    // document-scoped — may exist outside the component root
    this.burgerButtons =
      document.querySelectorAll<HTMLElement>(SELECTORS.burgerButton);
    this.siteSearchButtons = document.querySelectorAll<HTMLElement>(
      SELECTORS.siteSearchButton,
    );
  }

  init(): void {
    // Ensure panels are visible on init (matches jQuery .show() in original start())
    if (this.burgerFlyout) this.burgerFlyout.style.display = "";
    if (this.siteSearchFlyout) this.siteSearchFlyout.style.display = "";

    this.bindEvents();
    LoggedUserIndicator.updateLoginIndicator(this.el);
  }

  destroy(): void {
    this.abortController.abort();
  }

  // ─── Event binding ──────────────────────────────────────────────────────────

  private bindEvents(): void {
    const { signal } = this.abortController;

    // Close buttons — document-scoped (may live outside the component root)
    this.closeButtons.forEach((btn) => {
      btn.addEventListener(
        "click",
        (e) => {
          this.closeFlyout();
          e.preventDefault();
        },
        { signal },
      );
    });

    // Burger buttons — open nav flyout
    this.burgerButtons.forEach((burger) => {
      burger.addEventListener(
        "click",
        (e) => {
          this.openFlyout();
          this.setFlyoutType("nav");
          if (this.burgerFlyout) this.burgerFlyout.style.display = "";
          burger.querySelector("a")?.classList.add(CLASSES.active);
          e.preventDefault();
        },
        { signal },
      );
    });

    // Site-search buttons — open search flyout
    this.siteSearchButtons.forEach((btn) => {
      btn.addEventListener(
        "click",
        (e) => {
          this.pushData();
          this.openFlyout();
          this.setFlyoutType("site-search");
          if (this.siteSearchFlyout) this.siteSearchFlyout.style.display = "";
          e.preventDefault();
          setTimeout(() => this.siteSearchInput?.focus(), 200);
        },
        { signal },
      );
    });

    // Escape key
    document.addEventListener(
      "keyup",
      (e: KeyboardEvent) => {
        if (e.key === "Escape") this.closeFlyout();
      },
      { signal },
    );

    // Inter-module events (dispatched as native CustomEvents on document
    // by other migrated ES6 modules to replace Terrific _events bus)
    document.addEventListener("closeFlyout", () => this.closeFlyout(), {
      signal,
    });
    document.addEventListener("hideFlyout", () => this.hideFlyout(), {
      signal,
    });

    // Delegated document-level click handling
    document.addEventListener(
      "click",
      (e) => this.handleDocumentClick(e),
      { signal },
    );
  }

  // ─── Document click delegation ───────────────────────────────────────────────

  private handleDocumentClick(e: MouseEvent): void {
    const target = e.target as Element;

    if (this.hasSingleLevelNav) {
      // Click on outer-container inside flyout header → close
      const outerContainer = target.closest(".outer-container");
      if (outerContainer?.closest(".comp-flyout-header")) {
        this.closeFlyout();
        return;
      }

      // Click outside .flyout-type-nav while open → close
      // Burger button is excluded to avoid immediately re-closing after open
      if (
        this.el.classList.contains("flyout-type-nav") &&
        !this.el.classList.contains(CLASSES.closed) &&
        !this.el.contains(target) &&
        !target.closest(".burger")
      ) {
        this.closeFlyout();
        return;
      }
    }

    // Click outside site-search panel content → hide
    // Excludes the trigger button to prevent immediate re-close after open
    if (
      this.el.classList.contains("flyout-type-site-search") &&
      !target.closest(SELECTORS.siteSearchButton) &&
      !target.closest(SELECTORS.siteSearchFlyout)
    ) {
      this.hideFlyout();
    }
  }

  // ─── Flyout state ────────────────────────────────────────────────────────────

  private openFlyout(): void {
    document.body.style.overflow = "hidden";
    if (this.burgerFlyout) this.burgerFlyout.style.display = "none";
    if (this.siteSearchFlyout) this.siteSearchFlyout.style.display = "none";
    this.el.classList.remove(CLASSES.closed);
  }

  private closeFlyout(): void {
    if (
      this.siteSearchFlyout &&
      isVisible(this.siteSearchFlyout) &&
      this.siteSearchInput
    ) {
      this.siteSearchInput.value = "";
    }

    document.body.style.overflow = "auto";
    this.burgerButtons.forEach((burger) =>
      burger.querySelector("a")?.classList.remove(CLASSES.active),
    );
    this.el.classList.add(CLASSES.closed);

    const hashParams = new URLSearchParams(
      window.location.hash.substring(1),
    );
    hashParams.delete(URL_PARAM_SEARCH);
    hashParams.delete("c");
    window.location.hash = hashParams.toString();
  }

  private hideFlyout(): void {
    document.body.style.overflow = "auto";
    this.el.classList.add(CLASSES.closed);
  }

  // ─── Flyout type ─────────────────────────────────────────────────────────────

  private setFlyoutType(type: string): void {
    const flyoutTypeClasses = Array.from(this.el.classList).filter((c) =>
      c.startsWith("flyout-type-"),
    );
    if (flyoutTypeClasses.length > 0) {
      this.el.classList.remove(...flyoutTypeClasses);
    }
    this.el.classList.add(`flyout-type-${type}`);
  }

  // ─── UDL push ────────────────────────────────────────────────────────────────

  private pushData(): void {
    handleCustomUDL("search_start");
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="flyout"]')
      .forEach((el) => {
        new Flyout(el).init();
      });
  }
}
